import { t, ta } from "../../../lib/sprache";

/**
 * Kündigungsweg in fünf Schritten (Entscheidung Inhaber 21.10.2026), Fachlogik ohne Oberfläche:
 *  S1 „Das verlierst du“ (echte Zahlen), S2 „Pausieren statt kündigen“ (im Test: Einrichtung durch uns; beim Jahresabo: kleinerer
 *  Tarif zum nächsten Jahr bzw. übersprungen), S3 Grund (Pflicht, ein Klick), S4 Angebot passend zum Grund, S5 Zusammenfassung.
 * Leitplanken: kein Angebot doppelt, Genutztes (Rabatt einmal je Konto, Pause einmal in 12 Monaten) wird nicht angeboten, nichts
 * vorangekreuzt, auf jedem Schritt „Weiter zur Kündigung“ an derselben Stelle. Gespeichert wird je Durchlauf (public.cancel_flows).
 */

export const GRUENDE = ["zu_teuer", "funktion_fehlt", "keine_zeit", "wenig_kooperationen", "anderes_werkzeug", "sonstiges"] as const;
export type Grund = (typeof GRUENDE)[number];

export function grundLabel(g: Grund, imTest = false): string {
  switch (g) {
    case "zu_teuer": return t("Zu teuer", "Too expensive");
    case "funktion_fehlt": return t("Eine Funktion fehlt", "A feature is missing");
    case "keine_zeit": return imTest ? t("Keine Zeit zum Testen", "No time to try it") : t("Keine Zeit", "No time");
    case "wenig_kooperationen": return ta("Gerade wenig Kooperationen", "Few collaborations right now", "Gerade wenig Deals", "Few deals right now");
    case "anderes_werkzeug": return t("Anderes Werkzeug", "Another tool");
    default: return t("Sonstiges", "Other");
  }
}
/** Deutscher Text des Grunds für org_lifecycle.cancel_reason (wie bisher: gespeichert wird Deutsch) */
export function grundDeutsch(g: Grund): string {
  return ({ zu_teuer: "Zu teuer", funktion_fehlt: "Eine Funktion fehlt", keine_zeit: "Keine Zeit", wenig_kooperationen: "Gerade wenig Kooperationen", anderes_werkzeug: "Anderes Werkzeug", sonstiges: "Sonstiges" } as const)[g];
}

export const ANGEBOTE = ["pause", "rabatt", "kleiner", "jaehrlich", "funktion", "einrichtung", "gespraech", "kleiner_naechstes_jahr"] as const;
export type Angebot = (typeof ANGEBOTE)[number];

export type Einstieg = "kuendigen" | "test_beenden" | "konto_loeschen";

/** Was für dieses Konto überhaupt möglich ist (aus Lebenszyklus, Katalog und Zahlungsweg) */
export interface WegLage {
  imTest: boolean;
  jaehrlich: boolean;
  paypal: boolean;
  /** Pause möglich (app.pause_state.can_pause) */
  pauseMoeglich: boolean;
  /** Rückhalte-Rabatt schon genutzt (einmal je Konto) */
  rabattGenutzt: boolean;
  /** Rabatt technisch möglich (Stripe-Monatsabo, Test, Demo) */
  rabattMoeglich: boolean;
  /** kleinerer Tarif vorhanden */
  kleinerVorhanden: boolean;
  /** P-7: Kündigungsweg während einer laufenden Pause (kein Tarifwechsel, Rabatt erst ab dem Ende der Pause) */
  pausiert?: boolean;
}

/** Inhalt von Schritt 2: Pause, im Test die Einrichtung, beim Jahresabo der kleinere Tarif zum nächsten Jahr, sonst nichts (überspringen) */
export function schritt2(l: WegLage): Angebot | null {
  if (l.imTest) return "einrichtung";
  if (l.jaehrlich) return l.kleinerVorhanden ? "kleiner_naechstes_jahr" : null;
  return l.pauseMoeglich ? "pause" : null;
}

const JE_GRUND: Record<Grund, readonly Angebot[]> = {
  zu_teuer: ["rabatt", "kleiner", "jaehrlich"],
  funktion_fehlt: ["funktion", "rabatt"],
  keine_zeit: ["einrichtung", "pause"],
  wenig_kooperationen: ["pause"],
  anderes_werkzeug: ["kleiner"],
  sonstiges: ["gespraech"],
};

function moeglich(a: Angebot, l: WegLage): boolean {
  switch (a) {
    case "pause": return !l.imTest && l.pauseMoeglich;
    case "rabatt": return l.rabattMoeglich && !l.rabattGenutzt;
    // P-7: in der laufenden Pause kein Wechsel von Tarif oder Abrechnung (die Pause sperrt beides)
    case "kleiner": case "kleiner_naechstes_jahr": return l.kleinerVorhanden && !l.pausiert;
    case "jaehrlich": return !l.jaehrlich && !l.paypal && !l.pausiert;
    default: return true;
  }
}

/**
 * Angebote in Schritt 4 zum Grund: ohne Doppelte (was Schritt 2 schon gezeigt hat, entfällt; „kleiner zum nächsten Jahr“ gilt als
 * „kleiner“) und ohne Genutztes. Leer = Schritt 4 entfällt.
 */
export function angeboteFuer(g: Grund, l: WegLage, schonGezeigt: readonly Angebot[]): Angebot[] {
  const gezeigt = new Set<Angebot>(schonGezeigt);
  if (gezeigt.has("kleiner_naechstes_jahr")) gezeigt.add("kleiner");
  return JE_GRUND[g].filter((a) => !gezeigt.has(a) && moeglich(a, l));
}

/** Schrittfolge eines Durchlaufs: entfallende Schritte werden übersprungen, die Zählung bleibt „x von 5“ */
export function naechsterSchritt(aktuell: number, l: WegLage, grund: Grund | null, gezeigt: readonly Angebot[]): number {
  let n = aktuell + 1;
  if (n === 2 && !schritt2(l)) n = 3;
  if (n === 4 && (!grund || angeboteFuer(grund, l, gezeigt).length === 0)) n = 5;
  return Math.min(n, 5);
}

/** Ergebnis des Durchlaufs für die Auswertung, wenn „Endgültig kündigen“ geklickt wurde */
export function ergebnisKuendigung(e: Einstieg): "gekuendigt" | "loeschung_vorgemerkt" | "test_beendet" {
  return e === "konto_loeschen" ? "loeschung_vorgemerkt" : e === "test_beenden" ? "test_beendet" : "gekuendigt";
}

/** Fortschritt „Schritt x von 5“ */
export function schrittText(n: number): string {
  return t(`Schritt ${n} von 5`, `Step ${n} of 5`);
}

/** Rabatttext: bei zahlenden Kunden „die nächsten“, im Test „die ersten“ 3 Monate (K-2); in der Pause ab ihrem Ende (P-7) */
export function rabattLaufzeit(imTest: boolean, pausiert = false): string {
  if (pausiert) return t("für 3 Monate ab dem Ende der Pause", "for 3 months from the end of the pause");
  return imTest ? t("für die ersten 3 Monate", "for your first 3 months") : t("für die nächsten 3 Monate", "for the next 3 months");
}

/** P-6: Satz bis zum Ende, wenn der Kündigungsweg während einer laufenden Pause geöffnet wird */
export function bisDahinSatz(pausiert: boolean): string {
  return pausiert ? t("Bis dahin bleibt das Konto pausiert und nur lesbar.", "Until then, your account stays paused and read-only.")
    : t("Bis dahin arbeitest du normal weiter.", "Until then you keep working as usual.");
}

/** P-8: in der Pause gekündigt: das Abo endet mit dem Ende der Pause */
export function pauseKuendigungSatz(bis: string): string {
  return t(`Pausiert bis ${bis}, dann endet das Abo.`, `Paused until ${bis}, then the subscription ends.`);
}

/** „Anderes Werkzeug“: freiwillige Frage „Welches Werkzeug?“ in Schritt 3, unabhängig davon, ob Schritt 4 ein Angebot hat */
export function fragtWerkzeug(g: Grund): boolean {
  return g === "anderes_werkzeug";
}

/** Nullzeilen: Zeilen mit dem Wert 0 entfallen in Schritt 1; ist keine übrig, entfällt die Liste */
export function faktenZeilen<T extends { n: number }>(zeilen: readonly T[]): T[] {
  return zeilen.filter((z) => z.n > 0);
}

/** Titel des Dialogs je Einstieg */
export function wegTitel(e: Einstieg): string {
  return e === "test_beenden" ? t("Test beenden", "End trial") : e === "konto_loeschen" ? t("Konto löschen", "Delete account") : t("Abo kündigen", "Cancel subscription");
}

/** Satz in Schritt 2 vor der Pause (2c) */
export function pauseAusgaengeSatz(): string {
  return t("Mahnungen und Erinnerungen pausieren mit.", "Dunning emails and reminders pause too.");
}

/** Grund der fehlenden Pause in einem Satz (Abo-Seite) */
export function pauseNichtMoeglichText(grund: string | null | undefined, naechsteAm: string | null): string | null {
  switch (grund) {
    case "jahresabo": return t("Pausieren geht beim Monatsabo. Beim Jahresabo kannst du zum nächsten Jahr in einen kleineren Tarif wechseln.", "Pausing works with a monthly subscription. With an annual subscription you can switch to a smaller plan for the next year.");
    case "paypal": return t("Pausieren geht mit Kartenzahlung, nicht über PayPal: Dort ist nicht sicher, dass beim Fortsetzen nichts nachgebucht wird.", "Pausing works with card payment, not via PayPal: there it is not certain that nothing is charged retroactively when resuming.");
    case "genutzt": return naechsteAm ? t(`Pausieren geht einmal in 12 Monaten, wieder ab ${naechsteAm}.`, `Pausing is possible once every 12 months, again from ${naechsteAm}.`) : t("Pausieren geht einmal in 12 Monaten.", "Pausing is possible once every 12 months.");
    case "wechsel_vorgemerkt": return t("Es ist ein Tarifwechsel vorgemerkt. Nimm ihn zurück, dann kannst du pausieren.", "A plan change is scheduled. Withdraw it, then you can pause.");
    // 0425 (S-4): Ende des bezahlten Monats noch nicht bekannt (gleich nach dem Abschluss)
    case "abo_unklar": return t("Pausieren geht in ein paar Minuten, sobald das Ende deines bezahlten Monats feststeht.", "Pausing is possible in a few minutes, once the end of your paid month is known.");
    default: return null;
  }
}

/** Ein Satz, was nach dem Ende passiert (Schritt 1): 0425 Portal gesperrt, Export-Phase mit Frist aus dem Lebenszyklus */
export function danachSatz(imTest: boolean, tage: number): string {
  return imTest
    ? ta(`Nach dem Beenden ist das Portal sofort gesperrt. Mindestens ${tage} Tage lang kannst du aber alles exportieren.`, `After ending, the portal is locked right away. For at least ${tage} days you can still export everything.`,
      `Nach dem Beenden ist dein Konto sofort gesperrt. Mindestens ${tage} Tage lang kannst du aber alles exportieren.`, `After ending, your account is locked right away. For at least ${tage} days you can still export everything.`)
    : ta(`Nach dem Ende ist das Portal gesperrt. Mindestens ${tage} Tage lang kannst du aber alles exportieren, danach löschen wir mit Ankündigung.`, `After the end, the portal is locked. For at least ${tage} days you can still export everything; after that we delete with notice.`,
      `Nach dem Ende ist dein Konto gesperrt. Mindestens ${tage} Tage lang kannst du aber alles exportieren, danach löschen wir mit Ankündigung.`, `After the end, your account is locked. For at least ${tage} days you can still export everything; after that we delete with notice.`);
}

/** Kleinerer Tarif (gleiche Logik wie app.winback_offer) */
export function kleinererTarif(planKey: string | null | undefined): string | null {
  switch (planKey) {
    case "agentur": case "agentur_alt": return "team";
    case "team": case "team_alt": return "start";
    case "creator_pro": return "creator";
    default: return null;
  }
}

/** Angebot aus dem Link der Win-back-Mail (?angebot=…), nur bekannte Werte */
export function angebotAusLink(v: string | null): Angebot | null {
  return v && (ANGEBOTE as readonly string[]).includes(v) ? v as Angebot : null;
}
